import { FC, HTMLAttributes, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Checkbox, Flex, Form, Input } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import ProfileIcon from 'assets/images/menu/profile.svg?react';
import { useUpdateSettingsMutation } from 'entities/User';
import { EOnboardingSteps, OnboardingText, useOnboardingSlice } from 'features/Onboarding';
import { EInternalLinks, EYmEvents, EYmEventsGoals, EYmEventsO1, EYmEventsO2, EYmEventsO4 } from 'shared/typings';
import { sendYmEvent } from 'shared/utils';

interface IStepOneProps extends HTMLAttributes<HTMLDivElement> {}

export const StepOne: FC<IStepOneProps> = ({ className, ...props }) => {
	const { t } = useTranslation();
	const { setStep } = useOnboardingSlice();

	const [updateSettings, { status, isLoading }] = useUpdateSettingsMutation();

	const initialValues = { telegram: '', terms: false, agreement: false };

	const validationSchema = Yup.object({
		telegram: Yup.string()
			.required(t('Это поле обязательно для заполнения'))
			.max(64, t('Количество символов не должно превышать {{characters}}', { characters: 64 })),
		terms: Yup.bool().isTrue(t('Необходимо согласиться с пользовательским соглашением')),
		agreement: Yup.bool().isTrue(t('Необходимо согласиться с Политикой приватности')),
	});

	useEffect(() => {
		if (status === 'fulfilled') {
			sendYmEvent({
				event: EYmEvents.SEND_REGISTRATION_SUCCESS,
				o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER],
				o2: EYmEventsO2.REGISTRATION,
				o4: EYmEventsO4.PUBLISHER,
				reachGoal: EYmEventsGoals.SEND_REGISTRATION_SUCCESS_PUBLISHER,
			});
			setStep(EOnboardingSteps.SECOND);
		}
	}, [status]); // eslint-disable-line

	return (
		<Flex {...props} vertical gap={20} className={className}>
			<OnboardingText
				isSteps
				header={t('Укажите ваш Telegram-аккаунт')}
				text={t(
					'Контакт в мессенджере необходим для верификации вашего аккаунта и площадки в кабинете Affilead. Этот шаг обязателен.',
				)}
			/>

			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={({ telegram }) => {
					sendYmEvent({
						event: EYmEvents.CLICK_NEXT,
						o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, EYmEventsO1.WELCOME_POPUP],
						o2: EYmEventsO2.ADD_SOCIAL_ACCOUNT,
						o4: EYmEventsO4.TELEGRAM,
					});
					updateSettings({ telegram });
				}}>
				{({ values, errors, touched, handleSubmit, getFieldProps }) => {
					return (
						<form onSubmit={handleSubmit}>
							<Flex vertical gap={24}>
								<Flex vertical gap={16}>
									<Form.Item
										validateStatus={errors.telegram && touched.telegram ? 'error' : ''}
										help={
											errors.telegram && touched.telegram
												? errors.telegram
												: t('В случае предоставления некорректных данных ваш аккаунт может быть заблокирован')
										}>
										<Input
											prefix={<ProfileIcon style={{ opacity: 0.25 }} />}
											placeholder="Telegram-аккаунт"
											{...getFieldProps('telegram')}
											disabled={isLoading}
										/>
									</Form.Item>

									<Form.Item
										validateStatus={errors.terms && touched.terms ? 'error' : ''}
										help={errors.terms && touched.terms && errors.terms}>
										<Checkbox {...getFieldProps('terms')} disabled={isLoading}>
											<Trans i18nKey="termsCheckboxLabel">
												Я принимаю условия{' '}
												<a href={EInternalLinks.TERMS_OF_USE} target="_blank">
													Пользовательского соглашения
												</a>{' '}
												и подтверждаю, что прочитал и понял их.
											</Trans>
										</Checkbox>
									</Form.Item>

									<Form.Item
										validateStatus={errors.agreement && touched.agreement ? 'error' : ''}
										help={errors.agreement && touched.agreement && errors.agreement}>
										<Checkbox {...getFieldProps('agreement')} disabled={isLoading}>
											<Trans i18nKey="agreementCheckboxLabel">
												Я даю согласие на обработку моих персональных данных и подтверждаю, что прочитал и понял{' '}
												<a href={EInternalLinks.PRIVACY} target="_blank">
													Политику приватности
												</a>
												.
											</Trans>
										</Checkbox>
									</Form.Item>
								</Flex>

								<Flex justify="end">
									<Button
										type="primary"
										htmlType="submit"
										loading={isLoading}
										disabled={isLoading || !!Object.keys(errors).length || !values.telegram}>
										{t('Следующий')}
									</Button>
								</Flex>
							</Flex>
						</form>
					);
				}}
			</Formik>
		</Flex>
	);
};
